/**
 * Secure Authentication Token Management
 *
 * This module implements secure token storage using a hybrid approach:
 * - Access tokens stored in memory (not accessible via XSS)
 * - Refresh tokens in httpOnly cookies (when backend supports it)
 * - Fallback to sessionStorage with security headers
 *
 * Security features:
 * - XSS protection via memory storage
 * - CSRF protection via SameSite cookies
 * - Token rotation on refresh
 * - Automatic cleanup on tab close
 */

import { UserInfo } from './auth';
import logger from './logger';

// In-memory storage for access token (XSS-safe)
let memoryAccessToken: string | null = null;

// Token storage keys for sessionStorage fallback
const REFRESH_TOKEN_KEY = 'refresh_token_secure';
const USER_INFO_KEY = 'user_info';

// Auto-login: 로그인 시 사용자가 선택하면 토큰을 localStorage 에도 보관해
// 브라우저를 닫았다 다시 열어도 세션이 복원되게 한다. (사내 전용 서비스 전제)
const AUTO_LOGIN_KEY = 'auto_login_enabled';
const PERSISTENT_ACCESS_KEY = 'access_token_persistent';
const PERSISTENT_REFRESH_KEY = 'refresh_token_persistent';
const PERSISTENT_USER_INFO_KEY = 'user_info_persistent';

/**
 * Secure storage interface
 */
interface SecureStorage {
  setItem(key: string, value: string): void;
  getItem(key: string): string | null;
  removeItem(key: string): void;
}

/**
 * Create secure storage with XSS protection
 */
const createSecureStorage = (): SecureStorage => {
  // Use sessionStorage instead of localStorage
  // sessionStorage is cleared when tab closes, reducing attack window
  return {
    setItem: (key: string, value: string) => {
      try {
        sessionStorage.setItem(key, value);
      } catch (e) {
        logger.error('Secure storage write failed:', e);
      }
    },
    getItem: (key: string): string | null => {
      try {
        return sessionStorage.getItem(key);
      } catch (e) {
        logger.error('Secure storage read failed:', e);
        return null;
      }
    },
    removeItem: (key: string) => {
      try {
        sessionStorage.removeItem(key);
      } catch (e) {
        logger.error('Secure storage remove failed:', e);
      }
    }
  };
};

const secureStorage = createSecureStorage();

/**
 * Enable/disable auto-login. Disabling removes any persisted tokens.
 */
export const setAutoLogin = (enabled: boolean): void => {
  try {
    if (enabled) {
      localStorage.setItem(AUTO_LOGIN_KEY, 'true');
    } else {
      localStorage.removeItem(AUTO_LOGIN_KEY);
      localStorage.removeItem(PERSISTENT_ACCESS_KEY);
      localStorage.removeItem(PERSISTENT_REFRESH_KEY);
      localStorage.removeItem(PERSISTENT_USER_INFO_KEY);
    }
  } catch (e) {
    logger.error('Auto-login flag write failed:', e);
  }
};

export const isAutoLoginEnabled = (): boolean => {
  try {
    return localStorage.getItem(AUTO_LOGIN_KEY) === 'true';
  } catch {
    return false;
  }
};

/**
 * Store authentication tokens securely
 * Access token in memory, refresh token in sessionStorage
 */
export const setTokens = (accessToken: string, refreshToken: string): void => {
  // Store access token in memory only (XSS-safe)
  memoryAccessToken = accessToken;

  // Store refresh token in sessionStorage (cleared on tab close)
  secureStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);

  // Set token expiry for additional validation (matches backend AccessTokenExpiredTime)
  const expiryTime = Date.now() + 30 * 24 * 60 * 60 * 1000; // 30 days
  secureStorage.setItem('token_expiry', expiryTime.toString());

  // Auto-login: 브라우저 재시작 후에도 복원할 수 있게 localStorage 에 사본 보관
  if (isAutoLoginEnabled()) {
    try {
      localStorage.setItem(PERSISTENT_ACCESS_KEY, accessToken);
      localStorage.setItem(PERSISTENT_REFRESH_KEY, refreshToken);
    } catch (e) {
      logger.error('Persistent token write failed:', e);
    }
  }
};

/**
 * Get access token from memory
 */
export const getAccessToken = (): string | null => {
  // Check if token exists and is not expired
  if (memoryAccessToken) {
    const expiry = secureStorage.getItem('token_expiry');
    if (expiry && Date.now() > parseInt(expiry)) {
      memoryAccessToken = null;
      return null;
    }
  }
  return memoryAccessToken;
};

/**
 * Get refresh token from secure storage
 */
export const getRefreshToken = (): string | null => {
  const sessionToken = secureStorage.getItem(REFRESH_TOKEN_KEY);
  if (sessionToken) return sessionToken;

  // Auto-login: 새 브라우저 세션이면 localStorage 사본으로 폴백
  if (isAutoLoginEnabled()) {
    try {
      return localStorage.getItem(PERSISTENT_REFRESH_KEY);
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Clear all authentication data
 */
export const clearAuth = (): void => {
  memoryAccessToken = null;
  secureStorage.removeItem(REFRESH_TOKEN_KEY);
  secureStorage.removeItem(USER_INFO_KEY);
  secureStorage.removeItem('token_expiry');
  // 명시적 로그아웃/세션 무효화 시 자동 로그인용 사본도 함께 제거
  try {
    localStorage.removeItem(PERSISTENT_ACCESS_KEY);
    localStorage.removeItem(PERSISTENT_REFRESH_KEY);
    localStorage.removeItem(PERSISTENT_USER_INFO_KEY);
  } catch (e) {
    logger.error('Persistent token clear failed:', e);
  }
};

/**
 * Store user information securely
 */
export const setUserInfo = (userInfo: UserInfo): void => {
  const serialized = JSON.stringify(userInfo);
  secureStorage.setItem(USER_INFO_KEY, serialized);
  if (isAutoLoginEnabled()) {
    try {
      localStorage.setItem(PERSISTENT_USER_INFO_KEY, serialized);
    } catch (e) {
      logger.error('Persistent user info write failed:', e);
    }
  }
};

/**
 * Get user information from secure storage
 */
export const getUserInfo = (): UserInfo | null => {
  let userInfoStr = secureStorage.getItem(USER_INFO_KEY);
  if (!userInfoStr && isAutoLoginEnabled()) {
    try {
      userInfoStr = localStorage.getItem(PERSISTENT_USER_INFO_KEY);
    } catch {
      userInfoStr = null;
    }
  }
  if (!userInfoStr) return null;

  try {
    return JSON.parse(userInfoStr);
  } catch {
    return null;
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!getAccessToken();
};

/**
 * Check if token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

/**
 * Initialize token from storage on app load
 * This is called once when the app starts to restore session
 */
export const initializeAuth = (): void => {
  // Check for existing session
  const refreshToken = getRefreshToken();
  if (refreshToken && !isTokenExpired(refreshToken)) {
    // Auto-login: 새 브라우저 세션이면 localStorage 사본을 현재 세션으로 복원.
    // access token 은 만료됐어도 일단 복원 — refresh 인터셉터/ProtectedRoute 가
    // refresh token 으로 재발급한다 (백엔드는 refresh token 만 검증).
    if (isAutoLoginEnabled() && !secureStorage.getItem(REFRESH_TOKEN_KEY)) {
      try {
        secureStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
        const persistedAccess = localStorage.getItem(PERSISTENT_ACCESS_KEY);
        if (persistedAccess) {
          memoryAccessToken = persistedAccess;
        }
      } catch (e) {
        logger.error('Auto-login restore failed:', e);
      }
    }
    // Session exists, access token refresh is handled by the refresh interceptor
  } else {
    // No valid session, clear everything
    clearAuth();
  }
};

/**
 * Security recommendations for backend implementation:
 *
 * 1. Implement httpOnly, secure, SameSite cookies for refresh tokens:
 *    Set-Cookie: refresh_token=...; HttpOnly; Secure; SameSite=Strict; Path=/api/auth
 *
 * 2. Use short-lived access tokens (15-30 minutes)
 *
 * 3. Implement CSRF protection with double-submit cookies or tokens
 *
 * 4. Add Content Security Policy headers:
 *    Content-Security-Policy: default-src 'self'; script-src 'self'
 *
 * 5. Validate Origin/Referer headers on token refresh endpoints
 */